/** @jsxImportSource @emotion/react */
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import SignInTemplate from "../../templates/SignIn";
import Button from "../../components/Button";
import TextBox from "../../components/TextBox";
import { useGetSignup1Styles } from "./index.styles";
import googleIcon from "../../assets/images/svg/googleIcon.svg";
import {
  extractAuthData,
  fetchAuthenticatedUserProfile,
  getPostAuthRoute,
  normalizeOAuthProfile,
  parseOAuthProfile,
  persistUserSession,
  hasOAuthSession,
} from "../../utils/authSession";
import { requestGoogleProfile } from "../../utils/googleAuth";
import { REACT_APP_GOOGLE_CLIENT_ID, baseUrl } from "../../connections";
import { UserContext } from "../../context/UserContext";
import { useCookies } from "react-cookie";

const ERROR_MESSAGES = {
  cancelled: "Google sign in was cancelled. Please try again.",
  missing_client_id:
    "Google sign in is not configured. Add REACT_APP_GOOGLE_CLIENT_ID for this frontend.",
  popup_failed: "Google sign in could not open. Please try again.",
  profile_failed: "Google profile could not be loaded. Please try again.",
  script_failed:
    "Google sign in could not be loaded right now. Please try again.",
  access_denied: "Google sign in was denied. Please try again.",
  state_invalid: "Your sign in session expired. Please try again.",
  invalid_grant:
    "Google sign in could not be completed right now. Please try again.",
  invalid_client:
    "Google sign in is not configured correctly. Please contact support.",
  invalid_request: "Google sign in request is invalid. Please try again.",
  exchange_failed:
    "Google sign in could not be completed right now. Please try again.",
};

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const OTP_LENGTH = 6;
const REMEMBERED_EMAIL_KEY = "mm_signup_email";
const ALLOWED_RETURN_HOSTS = new Set([
  "stage-bingo.money-mails.com",
  "bingo.money-mails.com",
  "localhost",
]);
const EXISTING_EMAIL_MESSAGE_REGEX =
  /(already exists|already registered|already in use|account exists|user exists|email exists)/i;
const INITIAL_LOADING_STATE = {
  title: "Preparing Profile",
  text: "Signing in to Money Mails. Please wait.",
};

const parseApiResponse = async (response) => {
  const responseText = await response.text();
  let payload = null;

  try {
    payload = responseText ? JSON.parse(responseText) : null;
  } catch (_error) {
    payload = null;
  }

  if (!response.ok) {
    const message =
      payload?.message ||
      payload?.error ||
      responseText ||
      "Request could not be completed.";
    const isHtmlError =
      typeof responseText === "string" &&
      /<html[\s>]/i.test(responseText) &&
      /Cannot\s+(GET|POST|PUT|DELETE)\s+/i.test(responseText);
    const url = response?.url || "";
    const statusText = response?.statusText || "";

    throw new Error(
      `${
        response.status === 404 && isHtmlError
          ? "API endpoint not found. Check REACT_APP_API_URL / backend routes."
          : message
      } (HTTP ${response.status}${statusText ? ` ${statusText}` : ""}${
        url ? ` ${url}` : ""
      })`,
    );
  }

  return payload || {};
};

const getSafeReturnUrl = (search) => {
  const params = new URLSearchParams(search || "");
  const candidate = params.get("return_to") || params.get("returnTo") || "";

  if (!candidate) {
    return "";
  }

  try {
    const parsed = new URL(candidate);
    if (!ALLOWED_RETURN_HOSTS.has(parsed.hostname)) {
      return "";
    }
    return parsed.toString();
  } catch (_error) {
    return "";
  }
};

const Signup1 = () => {
  const classes = useGetSignup1Styles();
  const navigate = useNavigate();
  const location = useLocation();
  const otpInputRefs = useRef([]);
  const handledOAuthRedirect = useRef(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [loadingState, setLoadingState] = useState(INITIAL_LOADING_STATE);
  const [localAuthError, setLocalAuthError] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [manualEmailError, setManualEmailError] = useState("");
  const [manualStep, setManualStep] = useState("entry");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpValues, setOtpValues] = useState(Array(OTP_LENGTH).fill(""));
  const [otpError, setOtpError] = useState("");
  const [otpStatus, setOtpStatus] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [rememberEmail, setRememberEmail] = useState(false);
  const { setUserDetails } = useContext(UserContext);
  const [, setCookie] = useCookies([
    "dInfoUserEmail",
    "dInfoUserId",
    "dInfoUserType",
  ]);

  const authError = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const errorCode = params.get("auth_error");

    if (!errorCode) {
      return "";
    }

    return (
      ERROR_MESSAGES[errorCode] || "Google sign in failed. Please try again."
    );
  }, [location.search]);
  const returnTo = useMemo(
    () => getSafeReturnUrl(location.search),
    [location.search],
  );
  const loginRoute = returnTo
    ? `/login?return_to=${encodeURIComponent(returnTo)}`
    : "/login";

  useEffect(() => {
    const savedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY) || "";
    if (!savedEmail) {
      return;
    }

    setManualEmail(savedEmail);
    setRememberEmail(true);
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setResendCooldown((current) => Math.max(current - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const focusOtpInput = (index) => {
    window.setTimeout(() => {
      otpInputRefs.current[index]?.focus();
      otpInputRefs.current[index]?.select?.();
    }, 0);
  };

  const resetOtpState = () => {
    setOtpValues(Array(OTP_LENGTH).fill(""));
    setOtpError("");
  };

  const persistRememberedEmail = (email) => {
    if (rememberEmail) {
      localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
      return;
    }

    localStorage.removeItem(REMEMBERED_EMAIL_KEY);
  };

  const redirectExistingUserToLogin = (email) => {
    const normalizedEmail = String(email || "")
      .trim()
      .toLowerCase();

    if (normalizedEmail) {
      setManualEmail(normalizedEmail);
      persistRememberedEmail(normalizedEmail);
    }

    setManualStep("existing");
    setOtpError("");
    setOtpStatus("");
    setLocalAuthError(
      "An account with this email already exists. Please log in instead.",
    );
  };

  const registerAndRedirect = async (profile) => {
    try {
      if (hasOAuthSession(profile)) {
        setLoadingState({
          title: "Redirecting",
          text: "Your Google session is ready. Taking you to your account.",
        });
        persistUserSession({
          data: profile,
          setUserDetails,
          setCookie,
        });

        if (returnTo) {
          window.location.assign(returnTo);
          return;
        }

        navigate("/home", { replace: true });
        return;
      }

      setLoadingState({
        title: "Verifying Account",
        text: "Confirming your Google profile and preparing your Money Mails account.",
      });
      const response = await fetch(`${baseUrl}/auth/google/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...profile,
          accessToken: profile.accessToken || profile.access_token || "",
        }),
      });
      const result = await parseApiResponse(response);

      if (result?.data) {
        setLoadingState({
          title: "Redirecting",
          text: "Account is ready. Redirecting to the next step.",
        });
        persistUserSession({
          data: result.data,
          setUserDetails,
          setCookie,
        });

        if (returnTo) {
          window.location.assign(returnTo);
          return;
        }

        navigate(getPostAuthRoute(result.data), {
          replace: true,
        });
        return;
      }

      throw new Error(result?.message || "Google signup failed.");
    } catch (error) {
      console.error("Google registration failed", error);
      setIsRedirecting(false);
      setLoadingState(INITIAL_LOADING_STATE);
      if (EXISTING_EMAIL_MESSAGE_REGEX.test(error?.message || "")) {
        redirectExistingUserToLogin(profile?.email);
        return;
      }
      setLocalAuthError(
        error?.message || "Google signup failed. Please try again.",
      );
    }
  };

  const startGoogleSignup = async () => {
    setLocalAuthError("");
    setIsRedirecting(true);
    setLoadingState({
      title: "Opening Google Sign In",
      text: "Waiting for Google account selection to finish.",
    });

    try {
      const profile = normalizeOAuthProfile(
        await requestGoogleProfile({
          clientId: REACT_APP_GOOGLE_CLIENT_ID,
        }),
      );

      setLoadingState({
        title: "Google Sign In Complete",
        text: "Google account confirmed. Finishing Money Mails sign up.",
      });
      await registerAndRedirect(profile);
    } catch (error) {
      console.error("Google sign in failed", error);
      setIsRedirecting(false);
      setLoadingState(INITIAL_LOADING_STATE);
      setLocalAuthError(
        ERROR_MESSAGES[error?.code] ||
          error?.message ||
          "Google sign in failed. Please try again.",
      );
    }
  };

  const sendEmailOtp = async ({ isResend = false } = {}) => {
    const normalizedEmail = manualEmail.trim().toLowerCase();

    if (!normalizedEmail) {
      setManualEmailError("Enter your email address to continue.");
      return;
    }

    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setManualEmailError("Enter a valid email address.");
      return;
    }

    setManualEmailError("");
    setLocalAuthError("");
    setOtpError("");
    setOtpStatus("");
    setIsSendingOtp(true);

    try {
      const response = await fetch(`${baseUrl}/user/email/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: normalizedEmail,
        }),
      });
      const result = await parseApiResponse(response);
      const existingEmailDetected = Boolean(
        result?.data?.email_exists ??
        result?.data?.emailExists ??
        result?.data?.is_existing_user ??
        result?.data?.isExistingUser ??
        result?.data?.existing_user ??
        result?.data?.existingUser ??
        result?.data?.user_exists ??
        result?.data?.userExists,
      );

      setManualEmail(normalizedEmail);
      persistRememberedEmail(normalizedEmail);

      if (existingEmailDetected) {
        redirectExistingUserToLogin(normalizedEmail);
        return;
      }

      setManualStep("verify");
      resetOtpState();
      setOtpStatus(
        result?.message || `Verification code sent to ${normalizedEmail}.`,
      );
      setResendCooldown(Number(result?.data?.retryAfter) || 30);
      focusOtpInput(0);
    } catch (error) {
      if (
        !isResend &&
        EXISTING_EMAIL_MESSAGE_REGEX.test(error?.message || "")
      ) {
        redirectExistingUserToLogin(normalizedEmail);
        return;
      }

      if (isResend) {
        setOtpError(error?.message || "Code could not be resent.");
      } else {
        setLocalAuthError(error?.message || "Code could not be sent.");
      }
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleManualContinue = async (event) => {
    event.preventDefault();
    await sendEmailOtp();
  };

  const handleOtpInputChange = (event, index) => {
    const nextValue = String(event.target.value || "")
      .replace(/\D/g, "")
      .slice(-1);

    setOtpValues((current) => {
      const next = [...current];
      next[index] = nextValue;
      return next;
    });
    setOtpError("");

    if (nextValue && index < OTP_LENGTH - 1) {
      focusOtpInput(index + 1);
    }
  };

  const handleOtpKeyDown = (event, index) => {
    if (event.key === "Backspace" && !otpValues[index] && index > 0) {
      focusOtpInput(index - 1);
    }
  };

  const handleOtpPaste = (event) => {
    event.preventDefault();
    const pastedValue = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);

    if (!pastedValue) {
      return;
    }

    const next = Array(OTP_LENGTH).fill("");
    pastedValue.split("").forEach((value, index) => {
      next[index] = value;
    });
    setOtpValues(next);
    setOtpError("");

    const focusIndex = Math.min(pastedValue.length, OTP_LENGTH - 1);
    focusOtpInput(focusIndex);
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    const otp = otpValues.join("");

    if (otp.length !== OTP_LENGTH) {
      setOtpError("Enter the 6-digit code sent to your email.");
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError("");
    setLocalAuthError("");

    try {
      const response = await fetch(`${baseUrl}/user/email/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: manualEmail.trim().toLowerCase(),
          otp,
        }),
      });
      const result = await parseApiResponse(response);
      const authData = extractAuthData(result);
      const emailVerificationToken =
        result?.data?.email_verification_token || "";

      if (authData?.token) {
        const sessionData = await fetchAuthenticatedUserProfile(authData);

        persistUserSession({
          data: sessionData,
          setUserDetails,
          setCookie,
        });

        if (returnTo) {
          window.location.assign(returnTo);
          return;
        }

        navigate(getPostAuthRoute(sessionData), { replace: true });
        return;
      }

      if (!emailVerificationToken) {
        throw new Error("Email verification could not be completed.");
      }

      navigate(
        `/complete-profile?prefill_email=${encodeURIComponent(
          manualEmail.trim().toLowerCase(),
        )}&email_verification_token=${encodeURIComponent(emailVerificationToken)}`,
      );
    } catch (error) {
      setOtpError(
        error?.message || "Verification code is invalid. Please try again.",
      );
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  useEffect(() => {
    if (handledOAuthRedirect.current) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const oauthProfile = params.get("oauth_profile");

    if (!oauthProfile) {
      return;
    }

    try {
      setIsRedirecting(true);
      setLoadingState({
        title: "Restoring Session",
        text: "Finalizing your Google session from the callback response.",
      });
      const profile = normalizeOAuthProfile(parseOAuthProfile(oauthProfile));
      handledOAuthRedirect.current = true;

      if (!hasOAuthSession(profile)) {
        throw new Error("Missing authenticated session.");
      }

      setLoadingState({
        title: "Redirecting",
        text: "Session restored. Taking you to complete your profile.",
      });
      persistUserSession({
        data: profile,
        setUserDetails,
        setCookie,
      });
      setLocalAuthError("");

      if (returnTo) {
        window.location.assign(returnTo);
        return;
      }

      navigate(getPostAuthRoute(profile), { replace: true });
    } catch (error) {
      console.error("Failed to hydrate Google profile", error);
      handledOAuthRedirect.current = false;
      setIsRedirecting(false);
      setLoadingState(INITIAL_LOADING_STATE);
      setLocalAuthError(
        "Google signup could not be completed. Please try again.",
      );
    }
  }, [location.search, navigate, returnTo, setCookie, setUserDetails]);

  return (
    <SignInTemplate
      rightOverlay={
        isRedirecting ? (
          <div css={classes.loadingOverlayContent}>
            <div css={classes.loaderSpinner} aria-hidden="true">
              <span css={[classes.loaderDot, classes.loaderDot1]} />
              <span css={[classes.loaderDot, classes.loaderDot2]} />
              <span css={[classes.loaderDot, classes.loaderDot3]} />
              <span css={[classes.loaderDot, classes.loaderDot4]} />
              <span css={[classes.loaderDot, classes.loaderDot5]} />
              <span css={[classes.loaderDot, classes.loaderDot6]} />
              <span css={[classes.loaderDot, classes.loaderDot7]} />
              <span css={[classes.loaderDot, classes.loaderDot8]} />
            </div>
            <h3 css={classes.loadingOverlayTitle}>{loadingState.title}</h3>
            <p css={classes.loadingOverlayText}>{loadingState.text}</p>
          </div>
        ) : null
      }
    >
      <div css={classes.container}>
        <div css={classes.card}>
          <h2 css={classes.title}>Sign up for an Account</h2>

          {manualStep === "entry" ? (
            <>
              <div css={classes.providerRow}>
                <Button
                  type="button"
                  variant="contained"
                  css={classes.providerButton}
                  startIcon={
                    <img src={googleIcon} alt="" css={classes.providerIcon} />
                  }
                  onClick={startGoogleSignup}
                  disabled={isRedirecting}
                >
                  Google
                </Button>
              </div>

              <div css={classes.divider}>
                <span css={classes.dividerText}>or continue with email</span>
              </div>

              <form css={classes.manualForm} onSubmit={handleManualContinue}>
                <TextBox
                  color="#FFFFFF"
                  label="Primary Email"
                  type="email"
                  fullWidth
                  required
                  id="manual-email"
                  value={manualEmail}
                  onChange={(_event, value) => {
                    setManualEmail(value);
                    if (manualEmailError) {
                      setManualEmailError("");
                    }
                    if (localAuthError) {
                      setLocalAuthError("");
                    }
                  }}
                />
                {manualEmailError && (
                  <div css={classes.errorBox}>{manualEmailError}</div>
                )}
                <label css={classes.rememberRow}>
                  <input
                    type="checkbox"
                    checked={rememberEmail}
                    onChange={(event) => setRememberEmail(event.target.checked)}
                  />
                  <span>Remember this email</span>
                </label>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  css={classes.continueButton}
                  disabled={isSendingOtp}
                >
                  {isSendingOtp ? "Sending code..." : "Continue"}
                </Button>
              </form>
            </>
          ) : manualStep === "verify" ? (
            <div css={classes.verificationPanel}>
              <div css={classes.verificationHeader}>
                <div css={classes.emailPill}>{manualEmail}</div>
                <button
                  type="button"
                  css={classes.changeEmailButton}
                  onClick={() => {
                    setManualStep("entry");
                    setOtpError("");
                    setOtpStatus("");
                    setLocalAuthError("");
                  }}
                >
                  Change
                </button>
              </div>

              <form css={classes.manualForm} onSubmit={handleVerifyOtp}>
                <div css={classes.verificationCopy}>
                  <h3 css={classes.verificationTitle}>
                    Check your inbox and enter code
                  </h3>
                </div>
                <div css={classes.otpFieldsWrapper} onPaste={handleOtpPaste}>
                  {otpValues.map((value, index) => (
                    <input
                      key={`otp-${index}`}
                      ref={(element) => {
                        otpInputRefs.current[index] = element;
                      }}
                      type="text"
                      inputMode="numeric"
                      autoComplete={index === 0 ? "one-time-code" : "off"}
                      maxLength={1}
                      value={value}
                      css={classes.otpField}
                      onChange={(event) => handleOtpInputChange(event, index)}
                      onKeyDown={(event) => handleOtpKeyDown(event, index)}
                    />
                  ))}
                </div>
                {otpStatus && (
                  <div css={classes.successBox}>
                    Verification code sent to {manualEmail}
                  </div>
                )}
                {otpError && <div css={classes.errorBox}>{otpError}</div>}
                <div css={classes.verificationActions}>
                  <label css={classes.rememberRow}>
                    <input
                      type="checkbox"
                      checked={rememberEmail}
                      onChange={(event) =>
                        setRememberEmail(event.target.checked)
                      }
                    />
                    <span>Remember this email</span>
                  </label>
                  <button
                    type="button"
                    css={classes.resendLink}
                    onClick={() => sendEmailOtp({ isResend: true })}
                    disabled={resendCooldown > 0 || isSendingOtp}
                  >
                    {resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : isSendingOtp
                        ? "Sending..."
                        : "Resend code"}
                  </button>
                </div>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  css={classes.continueButton}
                  disabled={isVerifyingOtp}
                >
                  {isVerifyingOtp ? "Verifying..." : "Verify and continue"}
                </Button>
              </form>
            </div>
          ) : (
            <div css={classes.verificationPanel}>
              <div css={classes.verificationHeader}>
                <div css={classes.emailPill}>{manualEmail}</div>
                <button
                  type="button"
                  css={classes.changeEmailButton}
                  onClick={() => {
                    setManualStep("entry");
                    setLocalAuthError("");
                  }}
                >
                  Change
                </button>
              </div>

              <div css={classes.manualForm}>
                <div css={classes.verificationCopy}>
                  <h3 css={classes.verificationTitle}>
                    Account already exists
                  </h3>
                </div>
                <p css={classes.existingAccountCopy}>
                  This signup page is only for new users. Use the login page to
                  sign in with Google or your email and password.
                </p>
                <Button
                  type="button"
                  variant="contained"
                  fullWidth
                  css={classes.continueButton}
                  onClick={() => navigate(loginRoute, { replace: true })}
                >
                  Go to Login
                </Button>
              </div>
            </div>
          )}

          {authError && <div css={classes.errorBox}>{authError}</div>}
          {localAuthError && <div css={classes.errorBox}>{localAuthError}</div>}

          <p css={classes.terms}>
            Continue with Google or verify your email to create a new Money
            Mails account. Existing users should use the login page. By
            continuing, I agree to the{" "}
            <Link to="/terms" style={classes.inlineLink}>
              Terms and Conditions
            </Link>{" "}
            and have read the{" "}
            <Link to="/privacy" style={classes.inlineLink}>
              Privacy Statement
            </Link>
            .
          </p>

          <Button
            type="button"
            variant="text"
            color="secondary"
            css={classes.loginLink}
            onClick={() => navigate(loginRoute)}
          >
            Already have an account? Login
          </Button>
        </div>
      </div>
    </SignInTemplate>
  );
};

export default Signup1;
